import { ClearOutlined, SearchOutlined } from '@ant-design/icons';
import { offerStatuses } from '@features/offers/constants/offerStatuses.const';
import { useOfferUpsellFlowOptions } from '@features/offers/hooks/useOfferUpsellFlowOptions';
import { OfferProperties } from '@features/offers/interfaces/offersInterface';
import { useDebounce } from '@hooks';
import { Button, Col, Flex, Input, Select, Tooltip } from 'antd';
import { useEffect, useMemo, useState } from 'react';

import { DateRangePicker } from 'src/app/framework/atoms/DateRangePicker/DateRangePicker.atom';

interface OfferFiltersProps {
  offerProperties?: OfferProperties;
  upsellFlowOptions: ReturnType<typeof useOfferUpsellFlowOptions>;
  initialFilters?: Record<string, any>;
  onFilter: (filters: Record<string, any>) => void;
}

const tagsOptions = [
  { value: 'default', label: 'Base' },
  { value: 'currentDefault', label: 'Prioritária' },
];

export const OfferFilters = ({
  offerProperties,
  upsellFlowOptions,
  initialFilters,
  onFilter,
}: OfferFiltersProps) => {
  const [filters, setFilters] = useState<Record<string, any>>(
    initialFilters || {}
  );

  const searchFilterDebounce = useDebounce(filters.search ?? '', 900);

  const updateFilter = (key: string, value: any) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      const isEmptyStatus =
        key === 'status' &&
        (value === '' || (Array.isArray(value) && !value.length));

      if (isEmptyStatus) {
        newFilters[key] = '';
        return newFilters;
      }

      if (value === undefined || value === null || value === '') {
        delete newFilters[key];
      } else {
        newFilters[key] = value;
      }
      return newFilters;
    });
  };

  useEffect(() => {
    const filtersToSend = { ...filters };
    onFilter(filtersToSend);
  }, [
    searchFilterDebounce,
    filters.label,
    filters.typeId,
    filters.acquisitionId,
    filters.seasonalityId,
    filters.initDate,
    filters.endDate,
    filters.status,
    filters.flowId,
  ]);

  const statusValue = useMemo(() => {
    if (!filters.status) return [];
    if (Array.isArray(filters.status)) return filters.status;
    return filters.status.split(',');
  }, [filters.status]);

  const handleClearAllFilters = () => {
    setFilters({ status: '' });
  };

  return (
    <Flex gap={12} style={{ marginBottom: '4px' }} wrap>
      <Col flex={'1 1 260px'}>
        <Input
          key={`filter-offer-search`}
          placeholder={'Pesquisar por id ou nome'}
          suffix={<SearchOutlined />}
          value={filters.search}
          onChange={e => {
            updateFilter('search', e.target.value);
          }}
          style={{ flex: 1 }}
          allowClear
        />
      </Col>
      <Col flex={'0 220px'}>
        <Select
          style={{ width: '100%' }}
          placeholder={'Funil de upsell'}
          value={filters.flowId ? `${filters.flowId}` : undefined}
          options={upsellFlowOptions.options}
          loading={upsellFlowOptions.isLoading}
          showSearch
          allowClear
          filterOption={false}
          onSearch={upsellFlowOptions.setSearch}
          notFoundContent={null}
          onChange={value => {
            updateFilter('flowId', value);
          }}
        />
      </Col>
      <Col flex={'0 150px'}>
        <Select
          style={{ width: '100%' }}
          placeholder={'Labels'}
          options={tagsOptions}
          value={filters.label}
          onChange={value => {
            updateFilter('label', value);
          }}
          allowClear
        />
      </Col>
      <Col flex={'0 180px'}>
        <Select
          style={{ width: '100%' }}
          placeholder={'Tipo'}
          value={filters.typeId ? `${filters.typeId}` : undefined}
          options={
            offerProperties
              ? offerProperties.types.map(({ id, name }) => ({
                  value: `${id}`,
                  label: name,
                }))
              : []
          }
          onChange={value => {
            updateFilter('typeId', value);
          }}
          allowClear
        />
      </Col>
      <Col flex={'0 150px'}>
        <Select
          style={{ width: '100%' }}
          placeholder={'Aquisição'}
          value={filters.acquisitionId ? `${filters.acquisitionId}` : undefined}
          options={
            offerProperties
              ? offerProperties.acquisitionTypes.map(({ id, name }) => ({
                  value: `${id}`,
                  label: name,
                }))
              : []
          }
          onChange={value => {
            updateFilter('acquisitionId', value);
          }}
          allowClear
        />
      </Col>
      <Col flex={'0 150px'}>
        <Select
          style={{ width: '100%' }}
          placeholder={'Sazonalidade'}
          value={filters.seasonalityId ? `${filters.seasonalityId}` : undefined}
          options={
            offerProperties
              ? offerProperties.seasonalities.map(({ id, name }) => ({
                  value: `${id}`,
                  label: name,
                }))
              : []
          }
          onChange={value => {
            updateFilter('seasonalityId', value);
          }}
          allowClear
        />
      </Col>
      <Col flex={'0 300px'}>
        <DateRangePicker
          startValue={filters.initDate}
          endValue={filters.endDate}
          placeholder={['Data inicial', 'Data final']}
          onChange={dates => {
            if (dates && dates[0] && dates[1]) {
              updateFilter('initDate', dates[0]);
              updateFilter('endDate', dates[1]);
            } else {
              updateFilter('initDate', undefined);
              updateFilter('endDate', undefined);
            }
          }}
        />
      </Col>
      <Col flex={'0 150px'}>
        <Select
          style={{ width: '100%' }}
          placeholder={'Status'}
          value={statusValue}
          options={Object.keys(offerStatuses).map(key => ({
            value: key,
            label: offerStatuses[key as keyof typeof offerStatuses],
          }))}
          mode='multiple'
          onChange={value => {
            updateFilter('status', value.length ? value : '');
          }}
          allowClear
        />
      </Col>
      <Col>
        <Tooltip title={'Limpar filtros'}>
          <Button onClick={handleClearAllFilters} aria-label='Limpar filtros'>
            <ClearOutlined />
          </Button>
        </Tooltip>
      </Col>
    </Flex>
  );
};
